
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader, Card, Button, Table, Thead, Tbody, Tr, Th, Td, Input, CustomSelect, Toggle } from '../../components/UI';
import { Plus, Search, User, MoreHorizontal, Shield, Check } from '../../components/Icons';
import { usersData, User as UserType } from '../../data/users';
import { useTranslation } from '../../contexts/LanguageContext';
import { ChannelType } from '../../types';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [view, setView] = useState<'LIST' | 'FORM'>('LIST');
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState<Partial<UserType>>({
    name: '',
    email: '',
    role: 'Viewer',
    status: 'Active',
    authorizedChannels: []
  });

  useEffect(() => {
    if (location.state && location.state.editUser) {
      setFormData(location.state.editUser);
      setIsEditMode(true);
      setView('FORM');
    }
  }, [location]);

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredUsers = useMemo(() => {
    return usersData.filter(user => {
      // Global Search
      if (globalSearch) {
        const s = globalSearch.toLowerCase();
        const matchesGlobal = 
          user.name.toLowerCase().includes(s) || 
          user.email.toLowerCase().includes(s);
        if (!matchesGlobal) return false;
      }

      // Column Filters
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((user as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [globalSearch, columnFilters]);

  const handleChannelToggle = (channel: ChannelType) => {
    const current = formData.authorizedChannels || [];
    if (current.includes(channel)) {
      setFormData({ ...formData, authorizedChannels: current.filter(c => c !== channel) });
    } else {
      setFormData({ ...formData, authorizedChannels: [...current, channel] });
    }
  };

  const handleSave = () => {
    setView('LIST');
    setFormData({ name: '', email: '', role: 'Viewer', status: 'Active', authorizedChannels: [] });
    setIsEditMode(false);
    if (location.state?.editUser) {
        navigate(location.pathname, { replace: true, state: {} });
    }
  };

  const handleCancel = () => {
    setView('LIST');
    setIsEditMode(false);
    setFormData({ name: '', email: '', role: 'Viewer', status: 'Active', authorizedChannels: [] });
    if (location.state?.editUser) {
        navigate(location.pathname, { replace: true, state: {} });
    }
  };

  if (view === 'FORM') {
     // ... (Keep existing form render logic, simplifying for this update block to focus on table)
     return (
        <div className="p-6 max-w-[1400px] mx-auto animate-in fade-in duration-300 space-y-8">
           <PageHeader 
            title={isEditMode ? "Edit User" : "Create New User"}
            breadcrumbs={[{ label: 'Settings' }, { label: 'Users' }, { label: isEditMode ? 'Edit' : 'New' }]}
            onBack={handleCancel}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
             <div className="lg:col-span-2 space-y-8">
                <Card className="p-8 space-y-8">
                   <div className="flex items-center gap-3 pb-4 border-b border-gray-100 dark:border-[#333]">
                      <User size={20} className="text-brand-medium" />
                      <h3 className="text-sm font-bold text-brand-dark dark:text-white uppercase tracking-widest">User Details</h3>
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Full Name</label>
                         <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Email Address</label>
                         <Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Role</label>
                         <CustomSelect label="" value={formData.role || 'Viewer'} options={['Administrator', 'Accountant', 'Viewer']} onSelect={(val) => setFormData({...formData, role: val as any})} />
                      </div>
                      <div className="space-y-2 flex flex-col justify-end pb-2">
                         <Toggle label="Account Active" enabled={formData.status === 'Active'} onChange={(enabled) => setFormData({...formData, status: enabled ? 'Active' : 'Inactive'})} />
                      </div>
                   </div>
                </Card>
                <Card className="p-8 space-y-6">
                   <div className="flex items-center gap-3 pb-4 border-b border-gray-100 dark:border-[#333]">
                      <Shield size={20} className="text-brand-medium" />
                      <h3 className="text-sm font-bold text-brand-dark dark:text-white uppercase tracking-widest">Channel Permissions</h3>
                   </div>
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.values(ChannelType).map((channel) => (
                         <button key={channel} onClick={() => handleChannelToggle(channel)} className={`flex flex-col items-center justify-center gap-3 p-4 rounded-[6px] border transition-all ${formData.authorizedChannels?.includes(channel) ? 'bg-brand-medium/10 border-brand-medium text-brand-dark dark:text-white' : 'bg-gray-50 dark:bg-[#1A1A1A] border-gray-200 dark:border-[#333] text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'}`}>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${formData.authorizedChannels?.includes(channel) ? 'bg-brand-medium border-brand-medium text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                               {formData.authorizedChannels?.includes(channel) && <Check size={10} strokeWidth={4} />}
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider">{channel}</span>
                         </button>
                      ))}
                   </div>
                </Card>
             </div>
             <div className="space-y-6">
                <Card className="p-6 bg-brand-light/30 dark:bg-white/5 border-none">
                   <h4 className="text-xs font-bold uppercase tracking-widest text-brand-dark dark:text-white mb-4">Summary</h4>
                   <div className="space-y-4 text-sm"><div className="flex justify-between"><span className="text-gray-500">Name:</span><span className="font-medium text-brand-dark dark:text-white">{formData.name || '-'}</span></div><div className="flex justify-between"><span className="text-gray-500">Role:</span><span className="font-medium text-brand-dark dark:text-white">{formData.role}</span></div><div className="flex justify-between"><span className="text-gray-500">Status:</span><span className={`font-bold ${formData.status === 'Active' ? 'text-green-600' : 'text-gray-400'}`}>{formData.status}</span></div></div>
                </Card>
                <div className="flex flex-col gap-3"><Button onClick={handleSave} className="w-full shadow-lg">{isEditMode ? 'Update User' : 'Create User'}</Button><Button variant="ghost" onClick={handleCancel} className="w-full">Cancel</Button></div>
             </div>
          </div>
        </div>
     );
  }

  return (
    <div className="p-6 space-y-8 max-w-[1400px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title="User Management" 
        breadcrumbs={[{ label: 'Settings' }, { label: 'Users' }]}
        onBack={() => navigate('/dashboard')}
      />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative group w-full sm:w-80">
           <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-medium transition-colors" />
           <Input 
             placeholder="Search users..." 
             className="pl-10"
             value={globalSearch}
             onChange={(e) => setGlobalSearch(e.target.value)}
           />
        </div>
        <Button onClick={() => { setIsEditMode(false); setView('FORM'); }} className="flex items-center gap-2 shadow-lg">
           <Plus size={16} /> Add User
        </Button>
      </div>

      <Card className="overflow-hidden border-t-4 border-t-brand-medium p-0">
        <Table>
          <Thead>
            <Tr isHeader>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">User</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder="Filter Name/Email..."
                    onChange={(e) => handleColumnFilterChange('name', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Role</span>
                  <select 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                    onChange={(e) => handleColumnFilterChange('role', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Administrator">Administrator</option>
                    <option value="Accountant">Accountant</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                </div>
              </Th>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Status</span>
                  <select 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                    onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </Th>
              <Th className="align-top text-left">
                 <div className="flex flex-col gap-2 pb-2 h-full">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Channels Configured</span>
                    <div className="h-8"></div>
                 </div>
              </Th>
              <Th className="text-right align-top">
                 <div className="flex flex-col gap-2 pb-2 items-end h-full">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Actions</span>
                    <div className="h-8"></div>
                 </div>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredUsers.length > 0 ? filteredUsers.map((user) => (
              <Tr 
                key={user.id} 
                onClick={() => navigate(`/settings/users/${user.id}`)}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors"
              >
                <Td>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-light dark:bg-[#262626] border border-gray-200 dark:border-[#333] flex items-center justify-center text-xs font-bold text-brand-dark dark:text-white">
                      {user.avatarInitials}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-dark dark:text-white">{user.name}</p>
                      <p className="text-[10px] text-gray-500 font-medium">{user.email}</p>
                    </div>
                  </div>
                </Td>
                <Td>
                  <div className="flex items-center gap-1.5">
                    <Shield size={12} className="text-brand-medium" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{user.role}</span>
                  </div>
                </Td>
                <Td>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ${
                    user.status === 'Active' 
                      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30' 
                      : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-[#262626] dark:text-gray-400 dark:border-[#333]'
                  }`}>
                    {user.status}
                  </span>
                </Td>
                <Td>
                   <div className="flex items-center gap-1">
                      {user.authorizedChannels.map(ch => (
                        <span key={ch} className="text-[9px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 dark:bg-[#262626] px-1.5 py-0.5 rounded">
                          {ch}
                        </span>
                      ))}
                   </div>
                </Td>
                <Td className="text-right">
                  <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-full text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors">
                    <MoreHorizontal size={16} />
                  </button>
                </Td>
              </Tr>
            )) : (
              <Tr>
                <Td colSpan={5} className="text-center py-12 text-xs text-gray-400 uppercase tracking-widest font-bold">
                  No users found matching filters.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
};

export default UserManagement;
